/* eslint-disable max-len */

import React, { Fragment, useState } from 'react'
import { Form, Input, Button, Icon, Header } from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import { withTranslation } from 'react-i18next'

import ResendConfirmationCode from './subFeatures/ResendConfirmationCode'
import NeverReceivedConfirmationCode from './subFeatures/NeverReceivedConfirmationCode'

import InputNoAutoComplete from '../../../../common-components/semanticUiCustomComponents/InputNoAutoComplete'
import CapsLockIsOnWarning from '../../../../common-components/CapsLockIsOnWarning'

import {
  getInvalidPasswordErrorText,
  getIsStringAValidEmailAddress,
  getDoesStringContainDigitsOnly,
} from '../../../../utils'

import {
  FORGOT_PASSWORD_CONFIRMATION_CODE_MAX_LENGTH,
  LOGIN_EMAIL_MAX_LENGTH,
} from '../../../../config'

const ForgotPasswordResetPasswordForm = ({
  handleSubmit,
  isConfirmAccountForm,
  setEmail: parentSetEmail,
  setPassword: parentSetPassword,
  email, // the email address the user entered in step 1 of the Forgot Password form.
  isFetching,
  t: translate,
}) => {
  const [emailInput, setEmailInput] = useState('') // for confirm account only
  const [confirmationCodeInput, setConfirmationCodeInput] = useState('')
  const [enterPwInput, setEnterPwInput] = useState('')
  const [reEnterPwInput, setReEnterPwInput] = useState('')
  // gets set to true only when the user first enters a value into the input
  // fields
  const [emailInputTouched, setEmailInputTouched] = useState(false)
  const [confirmationCodeInputTouched, setConfirmationCodeInputTouched] = useState(false)
  const [enterPwInputTouched, setEnterPwInputTouched] = useState(false)
  const [reEnterPwInputTouched, setReEnterPwInputTouched] = useState(false)

  const [emailInputErrorText, setEmailInputErrorText] = useState(
    translate('Email address required')
  )
  const [confirmationCodeInputErrorText, setConfirmationCodeInputErrorText] = useState(
    translate('confirmationCodeRequired')
  )
  const [enterPwInputErrorText, setEnterPwInputErrorText] = useState(translate('Password required'))
  const [doPasswordsMatch, setDoPasswordsMatch] = useState(true) // "Enter" and "Re-Enter" will both be blank at first

  // for confirm account only
  const onEmailInputChange = e => {
    let nextEmailInput = e.target.value
    let nextEmailInputErrorText

    // remove all whitespace
    nextEmailInput = nextEmailInput && nextEmailInput.replace(/ /g, '')

    if (!getIsStringAValidEmailAddress(nextEmailInput)) {
      nextEmailInputErrorText = 'Invalid email address'
    }

    if (['', undefined].includes(nextEmailInput)) {
      nextEmailInputErrorText = translate('Email address required')
    }

    // max length rule
    if (nextEmailInput.length > LOGIN_EMAIL_MAX_LENGTH) {
      nextEmailInput = emailInput
    }

    setEmailInput(nextEmailInput)
    setEmailInputErrorText(nextEmailInputErrorText)
  }

  const onConfirmationCodeInputChange = e => {
    let nextConfirmationCodeInput = e.target.value
    let nextConfirmationCodeInputErrorText

    if (['', undefined].includes(nextConfirmationCodeInput)) {
      nextConfirmationCodeInputErrorText = translate('confirmationCodeRequired')
    }

    if (
      !getDoesStringContainDigitsOnly(nextConfirmationCodeInput) ||
      nextConfirmationCodeInput.length !== 6
    ) {
      nextConfirmationCodeInputErrorText = translate('confirmationCodeNot6DigitNumber')
    }

    // max length rule. Notice that max length is the only limiting rule for
    // this input. Confirmation codes sent by AWS are always 6 integers, but we
    // don't want to convey that to the end-user because that would guide
    // malicious users to what a valid confirmation code looks like.
    if (nextConfirmationCodeInput.length > FORGOT_PASSWORD_CONFIRMATION_CODE_MAX_LENGTH) {
      nextConfirmationCodeInput = confirmationCodeInput
    }

    setConfirmationCodeInput(nextConfirmationCodeInput)
    setConfirmationCodeInputErrorText(nextConfirmationCodeInputErrorText)
  }

  const onPasswordInputChange = e => {
    const nextEnterPwInput = e.target.value

    let nextEnterPwInputErrorText = getInvalidPasswordErrorText(nextEnterPwInput, translate) // null if valid

    if (['', undefined].includes(nextEnterPwInput)) {
      nextEnterPwInputErrorText = translate('Password required')
    }

    setEnterPwInput(nextEnterPwInput)
    setEnterPwInputTouched(true)
    setEnterPwInputErrorText(nextEnterPwInputErrorText)
    setDoPasswordsMatch(nextEnterPwInput === reEnterPwInput)
  }

  const onReEnterPasswordInputChange = e => {
    const nextReEnterPwInput = e.target.value

    setReEnterPwInput(nextReEnterPwInput)
    setReEnterPwInputTouched(true)
    setDoPasswordsMatch(nextReEnterPwInput === enterPwInput)
  }

  const onEmailInputBlur = () => {
    setEmailInputTouched(true)
  }

  const onConfirmationCodeInputBlur = () => {
    setConfirmationCodeInputTouched(true)
  }

  const onEnterPasswordInputsBlur = () => {
    setEnterPwInputTouched(true)
  }

  const onReEnterPasswordInputBlur = () => {
    setReEnterPwInputTouched(true)
  }

  const onSubmit = e => {
    e.preventDefault()
    if (isConfirmAccountForm) {
      parentSetEmail(emailInput) // CODE_COMMENTS_251
    }
    parentSetPassword(enterPwInput) // CODE_COMMENTS_251
    handleSubmit({
      email: isConfirmAccountForm ? emailInput : email,
      confirmationCode: confirmationCodeInput,
      password: enterPwInput,
    })
  }

  const doFormInputsHaveErrors = isConfirmAccountForm
    ? Boolean(emailInputErrorText) ||
      Boolean(confirmationCodeInputErrorText) ||
      Boolean(enterPwInputErrorText) ||
      !doPasswordsMatch
    : Boolean(confirmationCodeInputErrorText) || Boolean(enterPwInputErrorText) || !doPasswordsMatch

  return (
    <div>
      {!isConfirmAccountForm && (
        <div>
          <Header as="h4" color="grey">
            {translate('passwordResetStep2Title')}
          </Header>
          <p style={{ fontWeight: 'bold' }}>
            {`${translate('passwordResetConfirmationCodeSent')} ${email}`}
          </p>
          <p
            // for whatever reason, the popup below invalidates the default
            // bottom padding of <p> tags. We reinstate it here.
            style={{ paddingBottom: '1rem' }}
          >
            {translate('passwordResetSegmentBody')}
            <br />
            <NeverReceivedConfirmationCode />
          </p>
        </div>
      )}
      {isConfirmAccountForm && (
        <Fragment>
          <span>{translate('confirmAccountFormSegmentBody')}</span>
          <ResendConfirmationCode
            email={isConfirmAccountForm ? emailInput : email}
            isConfirmAccountForm={isConfirmAccountForm}
          />
        </Fragment>
      )}
      <Form onSubmit={onSubmit}>
        {isConfirmAccountForm && (
          <div className="field-with-error-message">
            <Form.Input
              label={translate('Email')}
              type="text"
              name="email"
              value={emailInput}
              onChange={onEmailInputChange}
              onBlur={onEmailInputBlur}
            />
            {emailInputTouched && emailInputErrorText ? (
              <small>{translate(emailInputErrorText)}</small>
            ) : null}
          </div>
        )}
        <div className="field-with-error-message">
          <Form.Field>
            <label htmlFor="confirmationCode">{translate('confirmationCodeFieldLabel')}</label>
            <InputNoAutoComplete
              name="confirmationCode"
              value={confirmationCodeInput}
              onChange={onConfirmationCodeInputChange}
              onBlur={onConfirmationCodeInputBlur}
            />
          </Form.Field>
          {confirmationCodeInputTouched && confirmationCodeInputErrorText ? (
            <small>{translate(confirmationCodeInputErrorText)}</small>
          ) : null}
        </div>
        <div className="field-with-error-message">
          <Form.Field>
            <label htmlFor="enterPassword">{translate('newPasswordFieldLabel')}</label>
            <CapsLockIsOnWarning
              translate={translate}
              trigger={
                <Input
                  name="enterPassword"
                  type="password"
                  value={enterPwInput}
                  onChange={onPasswordInputChange}
                  onBlur={onEnterPasswordInputsBlur}
                />
              }
            />
          </Form.Field>
          {enterPwInputTouched && enterPwInputErrorText ? (
            <small>{translate(enterPwInputErrorText)}</small>
          ) : null}
          <Form.Field style={{ paddingTop: '0.5em' }}>
            <label htmlFor="reEnterPassword">{translate('confirmPasswordFieldLabel')}</label>
            <CapsLockIsOnWarning
              translate={translate}
              trigger={
                <Input
                  name="reEnterPassword"
                  type="password"
                  value={reEnterPwInput}
                  onChange={onReEnterPasswordInputChange}
                  onBlur={onReEnterPasswordInputBlur}
                />
              }
            />
          </Form.Field>
          {reEnterPwInputTouched && !doPasswordsMatch ? (
            <small>{translate('passwordsDoNotMatchError')}</small>
          ) : null}
        </div>
        {isFetching ? (
          <Button type="submit" fluid animated loading disabled>
            {translate('Loading')}
          </Button>
        ) : (
          <Button
            type="submit"
            fluid
            animated
            color={doFormInputsHaveErrors ? null : 'green'}
            disabled={doFormInputsHaveErrors}
          >
            <Button.Content visible>{translate('Submit')}</Button.Content>
            <Button.Content hidden>
              <Icon name="right arrow" />
            </Button.Content>
          </Button>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '25px 0 5px',
            fontSize: '13px',
          }}
        >
          <Link to="/login">{translate('backToLogin')}</Link>
        </div>
      </Form>
    </div>
  )
}

export default withTranslation()(ForgotPasswordResetPasswordForm)
